/**
 * 領獎身分守衛（純函式）。契約與判定序列見 spec/2026-10-19-reward-claim-identity-scope/design.md §5.2～§5.3。
 */

export const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0

/**
 * 由 token 導出不可逆身分識別碼（FNV-1a 32-bit → 8 位 hex）。
 * 原始 token 是可重放憑證，不得進入 Pinia state / Sentry payload（BR-4）。
 *
 * @param {string|null|undefined} token
 * @returns {string|null} 8 位 hex；無身分時為 null
 */
export const identityKey = (token) => {
  if (!isNonEmptyString(token)) {
    return null
  }

  let hash = 0x811c9dc5
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }

  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * 判定用的 receiveNo：非正數（含 0 與空值）視為「沒有 per-user 編號」，與 admit() 不登記 0 對齊。
 * 預覽與領獎必須用同一個正規化結果判定，否則同一筆可能在預覽被擋、到領獎卻放行（BR-7）。
 *
 * @param {string|number|null|undefined} receiveNo
 * @returns {number|undefined}
 */
export const toClaimReceiveNo = (receiveNo) => (Number(receiveNo) > 0 ? Number(receiveNo) : undefined)

export const CLAIM_BLOCK_REASON = Object.freeze({
  TRANSITIONING: 'transitioning',
  UNCONFIRMED: 'unconfirmed',
  NOT_ADMITTED: 'not-admitted',
  FOREIGN: 'foreign',
})

const ALLOWED = Object.freeze({ allowed: true })
const blocked = (reason) => ({ allowed: false, reason })

/**
 * 帶 receiveNo 的領獎是否可以送出。fail-closed：證明不了屬於目前身分就不送（BR-3）。
 *
 * @param {object} facts
 * @param {string|number|null} facts.receiveNo  無 → 只帶 activityNo 的領獎，後端以 token 判定身分，放行
 * @param {boolean} facts.isLoggedIn            false → 交給請求層的 ERR_A103 閘門，放行
 * @param {boolean} facts.isTransitioning       自動登入／回前景探測／背景 episode 期間
 * @param {string|null} facts.currentOwner      目前 token 經 /self 確認的 uid_site
 * @param {string|null|undefined} facts.sourceOwner 登記該 receiveNo 的 token 的 uid_site
 * @param {boolean} facts.isAdmitted            該 receiveNo 是否被任何資料來源登記過
 * @returns {{ allowed: true } | { allowed: false, reason: string }}
 */
export const evaluateClaim = ({ receiveNo, isLoggedIn, isTransitioning, currentOwner, sourceOwner, isAdmitted } = {}) => {
  if (receiveNo === undefined || receiveNo === null || receiveNo === '') return ALLOWED
  if (!isLoggedIn) return ALLOWED
  if (isTransitioning) return blocked(CLAIM_BLOCK_REASON.TRANSITIONING)
  if (!isNonEmptyString(currentOwner)) return blocked(CLAIM_BLOCK_REASON.UNCONFIRMED)
  if (!isAdmitted) return blocked(CLAIM_BLOCK_REASON.NOT_ADMITTED)
  if (sourceOwner !== currentOwner) return blocked(CLAIM_BLOCK_REASON.FOREIGN)
  return ALLOWED
}
